import { useState } from "react";
import { Card } from "@/components/ui/card";
import { BagHeader } from "./BagHeader";
import { BagDescription } from "./BagDescription";
import { BagMeasurements } from "./BagMeasurements";
import { BagImageViewer } from "./BagImageViewer";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { BagInfo as BagInfoType } from "../BagDetails";
import { CatalogPreviewModal } from "../CatalogPreviewModal";
import { useAdmin } from "@/providers/AdminProvider";
import { BagImageUpload } from "./BagImageUpload";
import { Button } from "@/components/ui/button";
import { Edit, PlusCircle } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";

interface BagInfoProps {
  bag: BagInfoType;
}

export const BagInfo = ({ bag }: BagInfoProps) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedCatalogImage, setSelectedCatalogImage] = useState<{
    title: string;
    url: string;
  } | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [description, setDescription] = useState(bag.description);
  const { isAdmin } = useAdmin();
  const { toast } = useToast();

  const { data: catalogPages } = useQuery({
    queryKey: ["bag-catalogs", bag.styleNumber],
    queryFn: async () => {
      const styleNumber = parseInt(bag.styleNumber);
      if (isNaN(styleNumber)) {
        console.error('Invalid style number:', bag.styleNumber);
        return [];
      }
      
      console.log('Querying catalog pages for style number:', styleNumber);
      
      const { data, error } = await supabase
        .from("catalog_pages")
        .select(`
          id,
          page_number,
          style_numbers,
          thumbnail_url,
          catalogs (
            id,
            title
          )
        `)
        .contains('style_numbers', [styleNumber]);

      if (error) {
        console.error('Error fetching catalog pages:', error);
        throw error;
      }

      console.log('Found catalog pages:', data);
      return data;
    },
  });

  const handleSaveDescription = async () => {
    try {
      const { error } = await supabase
        .from('coach_bag_real_data')
        .update({ description })
        .eq('id', bag.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Description updated successfully",
      });
      setIsEditing(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update description",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="animate-fadeIn space-y-6">
      <Card className="overflow-hidden bg-white/80 backdrop-blur-sm border-2 border-gold/20">
        <div className="p-6 space-y-6 text-left">
          <BagHeader
            styleNumber={bag.styleNumber}
            alternateStyleNumber={bag.alternateStyleNumber}
            name={bag.name}
            alternativeName={bag.alternativeName}
            mainImage={bag.mainImage}
            onImageClick={(image) => setSelectedImage(image)}
          />
          
          <div className="hidden sm:block">
            {isEditing ? (
              <div className="space-y-4">
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="min-h-[200px]"
                  placeholder="Enter bag description..."
                />
                <div className="flex justify-end gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setIsEditing(false);
                      setDescription(bag.description);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSaveDescription}>Save</Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <BagDescription 
                  description={bag.description}
                  bagId={bag.id}
                />
                {isAdmin && (
                  <Button 
                    variant="outline" 
                    onClick={() => setIsEditing(true)}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Description
                  </Button>
                )}
              </div>
            )}
          </div>
          
          <div className="block sm:hidden">
            <p className="text-sm text-muted-foreground mb-2">Description</p>
            <p className="whitespace-pre-wrap">{bag.description}</p>
          </div>
          
          <BagMeasurements measurements={bag.measurements} />
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {bag.colors && (
                <div>
                  <p className="text-sm text-muted-foreground">Known Available Colors</p>
                  <p className="font-medium">{bag.colors}</p>
                </div>
              )}

              <div>
                <p className="text-sm text-muted-foreground">Known Production Years</p>
                <p className="font-medium">{bag.years}</p>
              </div>
            </div>
          </div>

          {isAdmin && (
            <BagImageUpload styleNumber={bag.styleNumber} />
          )}
        </div>
      </Card>

      {catalogPages && catalogPages.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-xl font-display">Catalog Appearances</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {catalogPages.map((page) => {
              if (!page.thumbnail_url) return null;

              return (
                <Card
                  key={page.id}
                  className="p-4 cursor-pointer hover:shadow-lg transition-shadow flex flex-col h-[400px]"
                  onClick={() => setSelectedCatalogImage({
                    title: `${page.catalogs.title} - Page ${page.page_number}`,
                    url: page.thumbnail_url!
                  })}
                >
                  <div className="flex-1 flex items-center justify-center bg-muted rounded-lg overflow-hidden">
                    <img
                      src={page.thumbnail_url}
                      alt={`${page.catalogs.title} - Page ${page.page_number}`}
                      className="w-auto h-auto max-w-full max-h-[300px]"
                    />
                  </div>
                  <div className="mt-4">
                    <p className="text-center font-medium">{page.catalogs.title}</p>
                    <p className="text-center text-sm text-muted-foreground">Page {page.page_number}</p>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      <BagImageViewer
        selectedImage={selectedImage}
        onClose={() => setSelectedImage(null)}
      />

      {selectedCatalogImage && (
        <CatalogPreviewModal
          isOpen={!!selectedCatalogImage}
          onClose={() => setSelectedCatalogImage(null)}
          catalogTitle={selectedCatalogImage.title}
          imageUrl={selectedCatalogImage.url}
        />
      )}
    </div>
  );
};
